import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { auth } from "../../../firebase";
import { isNativeApp } from "../../../native/platform";
import { isEmbeddedBrowser } from "../../../firebase/oauthEnv";
import { useAuthStore } from "../../../store/useAuthStore";
import { useAuthWithFirebase } from "../../../hooks/useAuth";

// Web OAuth client of the turaq-kg Firebase project (the one Firebase's Google
// sign-in already uses). One Tap needs https://turaq.kg in its "Authorized
// JavaScript origins" in Google Cloud Console.
const GOOGLE_CLIENT_ID = "1071788888872-qes0scd841b58216vtj1k626bqrtsa04.apps.googleusercontent.com";
const GSI_SRC = "https://accounts.google.com/gsi/client";
// Pages where the prompt would get in the way of the page's own sign-in UI.
const SKIP = [/^\/(login|register|forgot-password|email-link)/];

type Gsi = {
  accounts: {
    id: {
      initialize: (o: Record<string, unknown>) => void;
      prompt: () => void;
      cancel: () => void;
    };
  };
};

let scriptPromise: Promise<void> | null = null;
const loadGsi = () =>
  (scriptPromise ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("gsi load failed"));
    };
    document.head.appendChild(s);
  }));

/**
 * Google One Tap: guests see "Continue as <name>" and sign in with one tap on
 * the page they are reading. Google itself rate-limits the prompt after a
 * dismissal, so it never nags.
 */
export function GoogleOneTap() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();
  const mutation = useAuthWithFirebase("google", { stay: true });
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;

  // In-app browsers (Instagram, Facebook…): Google blocks sign-in there anyway.
  const blocked =
    isAuthenticated || isNativeApp() || isEmbeddedBrowser() || SKIP.some((re) => re.test(pathname));

  useEffect(() => {
    if (blocked) return;
    let cancelled = false;
    let timer = 0;
    // One Tap is a nicety, not part of the page: load Google's script only
    // after the visitor first scrolls/taps/types, so it never competes with
    // the page load (and FedCM's "not signed in" console error doesn't fire
    // for visitors who just look and leave).
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    const onFirstInteraction = () => {
      events.forEach((e) => window.removeEventListener(e, onFirstInteraction));
      timer = window.setTimeout(show, 1000);
    };
    events.forEach((e) => window.addEventListener(e, onFirstInteraction, { passive: true, once: true }));
    const show = async () => {
      try {
        await loadGsi();
        const google = (window as unknown as { google?: Gsi }).google;
        if (cancelled || !google) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          use_fedcm_for_prompt: true,
          context: "signin",
          callback: async ({ credential }: { credential?: string }) => {
            if (!credential) return;
            try {
              const cred = await signInWithCredential(auth, GoogleAuthProvider.credential(credential));
              mutateRef.current(await cred.user.getIdToken());
            } catch (e) {
              console.warn("One Tap sign-in failed:", e);
            }
          },
        });
        google.accounts.id.prompt();
      } catch {
        /* blocked by an ad-blocker / offline — nothing to show */
      }
    };
    return () => {
      cancelled = true;
      events.forEach((e) => window.removeEventListener(e, onFirstInteraction));
      window.clearTimeout(timer);
      (window as unknown as { google?: Gsi }).google?.accounts.id.cancel();
    };
  }, [blocked]);

  return null;
}
